import React, { useCallback, useEffect, useState } from "react";
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isToday,
} from "date-fns";
import { ClipboardList, Sun, Sunset, Moon } from "lucide-react";
import api from "../../api/axios";
import DutyRoasterUpload from "./DutyRoasterUpload";

const DutyRoster = () => {
  const today = new Date();
  const start = startOfWeek(today, { weekStartsOn: 1 });
  const end = endOfWeek(today, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start, end });
  const [selectedDate, setSelectedDate] = useState(today);
  const [activeTab, setActiveTab] = useState("weekly");
  const [shifts, setShifts] = useState([]);
  const [roaster, setRoaster] = useState([]);
  const shiftIcons = {
    Morning: {
      icon: <Sun stroke="#0ea5e9" />,
      color: "duty-roster-shift-text1",
    },
    Afternoon: {
      icon: <Sunset stroke="#f59e0b" />,
      color: "duty-roster-shift-text2",
    },
    Night: {
      icon: <Moon stroke="#6366f1" />,
      color: "duty-roster-shift-text3",
    },
  };

  const fetchShifts = useCallback(async () => {
    try {
      const res = await api.get("/hrms/shifts");
      const data = res.data || [];
      const sortedData = [...data].sort((a, b) => a.id - b.id);
      setShifts(sortedData);
    } catch (error) {
      console.error("Failed to fetch shifts:", error);
    }
  }, []);

  const fetchDutyROasters = useCallback(async () => {
    try {
      const res = await api.get("/hrms/rosters");
      const data = res.data || [];
      setRoaster(data.data);
    } catch (error) {
      console.error("Failed to fetch shifts:", error);
    }
  }, []);

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    const [hour, minute] = timeString.split(":");
    const h = parseInt(hour, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const formattedHour = h % 12 || 12;
    return `${formattedHour}:${minute} ${ampm}`;
  };

  useEffect(() => {
    fetchShifts();
    fetchDutyROasters();
  }, [fetchShifts, fetchDutyROasters]);

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="duty-roster-manage-cont">
            <div class="d-flex align-items-center gap-2">
              <ClipboardList size={35} stroke="#302867" />
              <div class="d-flex flex-column">
                <h5 class="fs-4 fw-semibold m-0">Duty Roster Management</h5>
                <span class="text-muted fs-6">
                  View the weekly schedule or upload a new roster file. Current
                  week: Nov 3 - Nov 9, 2025
                </span>
              </div>
            </div>
          </div>

          <div className="duty-roster-tabs-container">
            {/* Tabs Header */}
            <div className="duty-roster-tabs">
              <button
                className={`duty-roster-tab-btn ${
                  activeTab === "weekly" ? "active" : ""
                }`}
                onClick={() => setActiveTab("weekly")}
              >
                Weekly
              </button>

              <button
                className={`duty-roster-tab-btn ${
                  activeTab === "upload" ? "active" : ""
                }`}
                onClick={() => setActiveTab("upload")}
              >
                Upload & Validate Roster
              </button>
            </div>

            {/* Tabs Content */}
            <div className="duty-roster-tab-content mt-4">
              {activeTab === "weekly" && (
                <>
                  <div className="duty-roster-weekly">
                    <div className="duty-roster-week-cont">
                      <div className="duty-roster-week-grid">
                        {days.map((day) => {
                          const isActive =
                            format(day, "yyyy-MM-dd") ===
                            format(selectedDate, "yyyy-MM-dd");
                          const isCurrentDay = isToday(day);

                          return (
                            <div
                              key={day.toString()}
                              className={`text-center px-3 py-2 rounded-3 ${
                                isActive
                                  ? "duty-active-day-btn"
                                  : isCurrentDay
                                  ? "bg-current-day"
                                  : "duty-inactive-day-btn"
                              }`}
                              onClick={() => setSelectedDate(day)}
                            >
                              <div className="fw-bold fs-5">
                                {format(day, "EEE")}
                              </div>
                              <div className="text-muted">
                                {format(day, "d MMM")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="row row-gap-3 mt-4">
                    {roaster && Object.keys(roaster).length > 0 ? (
                      (() => {
                        const dateKey = format(selectedDate, "yyyy-MM-dd");
                        const dayData = roaster[dateKey];

                        if (!dayData) {
                          return (
                            <div className="text-center text-muted py-4">
                              No roster data
                              {format(selectedDate, "d MMM yyyy")}
                            </div>
                          );
                        }

                        // Sort by shift code (M, A, N)
                        const sortedShifts = Object.keys(dayData).sort();

                        return sortedShifts.map((shiftCode) => {
                          const shiftBlock = dayData[shiftCode];
                          const shift = shiftBlock.shift;
                          const employees = shiftBlock.employees;

                          const iconData =
                            shiftIcons[
                              shift?.name?.toLowerCase().includes("morning")
                                ? "Morning"
                                : shift.name.toLowerCase().includes("afternoon")
                                ? "Afternoon"
                                : shift.name.toLowerCase().includes("night")
                                ? "Night"
                                : "Morning"
                            ];

                          return (
                            <div
                              key={shiftCode}
                              className="col-lg-4 col-sm-12 col-12"
                            >
                              <div className="duty-roster-shift-card">
                                <div className="d-flex align-items-center gap-2 justify-content-center">
                                  {iconData.icon}
                                  <h5
                                    className={`m-0 fw-semibold ${iconData.color}`}
                                  >
                                    {shift.name}{" "}
                                    <span className="text-muted">
                                      ({employees.length})
                                    </span>
                                  </h5>
                                </div>

                                {employees.length > 0 ? (
                                  <ul className="list-unstyled mt-3">
                                    {employees.map((emp) => (
                                      <li
                                        key={emp.id}
                                        className="text-center border-bottom py-2"
                                      >
                                        <strong>{emp.name}</strong>
                                        <div className="text-muted small">
                                          {formatTime(emp.start_time)} -{" "}
                                          {formatTime(emp.end_time)}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="m-0 text-muted text-center mt-2 py-4">
                                    No employees assigned.
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()
                    ) : (
                      <div className="text-center text-muted py-4">
                        No Data Found
                      </div>
                    )}
                  </div>
                </>
              )}

              {activeTab === "upload" && <DutyRoasterUpload />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DutyRoster;
